/**
 * AdminGestoresRendicionPage — Dashboard CEO de gestores pendientes de rendir.
 *
 * Muestra:
 *   - Tabla con todos los gestores ordenada desc por saldo pendiente.
 *   - Toggle "Enlazar wallet" por gestor (solo 1 a la vez).
 *   - Click en un gestor → detalle con movimientos + acciones "confirmar rendición".
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link2, CheckCircle2, ChevronRight, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';
import { Money } from '@/components/Money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGestoresRendicionPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/gestores/cuenta-corriente`, { headers: getAuthHeaders() });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cargar'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleLink = async (gestor_id, enable) => {
    try {
      await axios.post(`${API}/admin/gestores/${gestor_id}/link-ceo-wallet`,
        { enable }, { headers: getAuthHeaders() });
      toast.success(enable ? '🔗 Wallet enlazada — tus remesas debitan este gestor' : '🔓 Enlace desactivado');
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo'));
    }
  };

  const openDetail = async (gestor_id) => {
    try {
      const r = await axios.get(`${API}/admin/gestores/${gestor_id}/cuenta-corriente`, { headers: getAuthHeaders() });
      setDetail(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cargar'));
    }
  };

  const confirmRendicion = async (gestor_id, rid) => {
    try {
      await axios.post(`${API}/admin/gestores/${gestor_id}/cuenta-corriente/rendicion/${rid}/confirm`,
        {}, { headers: getAuthHeaders() });
      toast.success('Rendición confirmada — saldo CEO actualizado');
      await load();
      await openDetail(gestor_id);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo confirmar'));
    }
  };

  if (loading) return <p className="text-center text-slate-500 py-12">Cargando…</p>;

  const totalPendiente = items.reduce((s, i) => s + (i.saldo_pendiente_ceo || 0), 0);

  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="admin-gestores-rendicion-page">
      <Card className="p-5 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        <h2 className="text-xl font-bold text-slate-900">Cuenta corriente · Gestores</h2>
        <p className="text-sm text-slate-700 mt-1">
          Cuánto te debe cada gestor por el 25% de las remesas que cobró para tu cuenta.
          Cuando un gestor declara un depósito, aparece como "pendiente de confirmar" — al
          confirmar, se acredita en <b>tu wallet CEO</b>.
        </p>
        <div className="mt-3">
          <p className="text-[10px] uppercase text-blue-700">Total pendiente del ecosistema</p>
          <Money amount={totalPendiente} currency="USDT" role={user?.role} sign="hide" bold className="text-2xl" />
        </div>
      </Card>

      <Card className="p-0 border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase text-slate-600">
                <th className="px-4 py-3">Gestor</th>
                <th className="text-right">Ganancia CEO acum.</th>
                <th className="text-right">Rendido</th>
                <th className="text-right">Pendiente</th>
                <th className="text-center">Enlace CEO</th>
                <th className="text-right pr-4">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.gestor_id} className="border-t hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{g.gestor_name || '—'}</p>
                    <p className="text-[11px] text-slate-500">{g.gestor_email}</p>
                  </td>
                  <td className="text-right">
                    <Money amount={g.ganancia_ceo_acumulada} currency="USDT" role={user?.role} sign="hide" />
                  </td>
                  <td className="text-right">
                    <Money amount={g.depositos_confirmados} currency="USDT" role={user?.role} sign="hide" />
                  </td>
                  <td className="text-right">
                    <Money amount={g.saldo_pendiente_ceo} currency="USDT" role={user?.role} sign="hide" bold />
                  </td>
                  <td className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Switch
                        checked={!!g.is_ceo_linked}
                        onCheckedChange={(v) => toggleLink(g.gestor_id, v)}
                        data-testid={`link-toggle-${g.gestor_id.slice(0, 8)}`}
                      />
                      {g.is_ceo_linked && <Link2 className="w-3.5 h-3.5 text-emerald-600" />}
                    </div>
                  </td>
                  <td className="text-right pr-4">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(g.gestor_id)}>
                      Ver <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500 italic">Sin gestores aún.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal — Lateral */}
      {detail && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-end sm:items-center justify-center p-3" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold">{detail.gestor.name}</h3>
                <p className="text-xs text-slate-500">{detail.gestor.email}</p>
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Card className="p-3"><p className="text-[10px] uppercase text-slate-500">Pendiente CEO</p>
                <Money amount={detail.kpis.saldo_pendiente_ceo} currency="USDT" role={user?.role} sign="hide" bold className="text-lg" /></Card>
              <Card className="p-3"><p className="text-[10px] uppercase text-slate-500">Rendido total</p>
                <Money amount={detail.kpis.depositos_confirmados} currency="USDT" role={user?.role} sign="hide" bold className="text-lg" /></Card>
            </div>
            <h4 className="text-sm font-bold mb-2">Movimientos</h4>
            <div className="space-y-1.5">
              {detail.movements.map((m) => (
                <div key={m.id} className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded">
                  <span className="text-[10px] text-slate-500 w-28">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="flex-1">
                    <b className="text-slate-700">{m.entry_type}</b>
                    {' · '}<Money amount={m.amount_usdt} currency="USDT" role={user?.role} sign="hide" />
                    {m.description && <span className="text-slate-500"> · {m.description}</span>}
                  </span>
                  {m.entry_type === 'rendicion' && m.status === 'pending' && (
                    <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => confirmRendicion(detail.gestor.id, m.id)}
                      data-testid={`confirm-rend-btn-${m.id.slice(0,8)}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Confirmar
                    </Button>
                  )}
                </div>
              ))}
              {detail.movements.length === 0 && (
                <p className="text-sm text-slate-500 italic">Sin movimientos.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGestoresRendicionPage;
